// ─── Permission Constants ──────────────────────────────────────────────
// Mirror of backend permissions. Single source of truth for the frontend.
// Never use raw strings — always reference these constants.

export const PERMISSIONS = {
  // ─── Expense Intake Permissions ─────────────────────────────────
  INTAKE_CREATE: 'intake.create',
  INTAKE_READ: 'intake.read',
  INTAKE_UPDATE: 'intake.update',
  INTAKE_DELETE: 'intake.delete',

  // ─── Expense Permissions ─────────────────────────────────────────
  EXPENSE_CREATE: 'expense.create',
  EXPENSE_READ: 'expense.read',
  EXPENSE_UPDATE: 'expense.update',
  EXPENSE_DELETE: 'expense.delete',
  EXPENSE_SUBMIT: 'expense.submit',
  EXPENSE_APPROVE: 'expense.approve',
  EXPENSE_REJECT: 'expense.reject',
  EXPENSE_READ_ALL: 'expense.read_all',
  EXPENSE_CANCEL: 'expense.cancel',
  EXPENSE_CREATE_FOR_OTHERS: 'expense.create_for_others',

  // ─── Document Permissions ────────────────────────────────────────
  DOCUMENT_UPLOAD: 'document.upload',
  DOCUMENT_READ: 'document.read',
  DOCUMENT_DELETE: 'document.delete',
  DOCUMENT_REPROCESS: 'document.reprocess',

  // ─── Approval Permissions ────────────────────────────────────────
  APPROVAL_VIEW: 'approval.view',
  APPROVAL_APPROVE: 'approval.approve',
  APPROVAL_REJECT: 'approval.reject',
  APPROVAL_DELEGATE: 'approval.delegate',
  APPROVAL_BYPASS: 'approval.bypass',

  // ─── Report Permissions ──────────────────────────────────────────
  REPORT_VIEW: 'report.view',
  REPORT_CREATE: 'report.create',
  REPORT_EXPORT: 'report.export',
  REPORT_SCHEDULE: 'report.schedule',

  // ─── User Permissions ────────────────────────────────────────────
  USER_CREATE: 'user.create',
  USER_READ: 'user.read',
  USER_UPDATE: 'user.update',
  USER_DELETE: 'user.delete',
  USER_INVITE: 'user.invite',
  USER_MANAGE_ROLES: 'user.manage_roles',

  // ─── Role Permissions ────────────────────────────────────────────
  ROLE_CREATE: 'role.create',
  ROLE_READ: 'role.read',
  ROLE_UPDATE: 'role.update',
  ROLE_DELETE: 'role.delete',
  ROLE_ASSIGN: 'role.assign',

  // ─── Company Permissions ─────────────────────────────────────────
  COMPANY_CREATE: 'company.create',
  COMPANY_READ: 'company.read',
  COMPANY_UPDATE: 'company.update',
  COMPANY_DELETE: 'company.delete',
  COMPANY_MANAGE: 'company.manage',

  // ─── Department Permissions ──────────────────────────────────────
  DEPARTMENT_CREATE: 'department.create',
  DEPARTMENT_READ: 'department.read',
  DEPARTMENT_UPDATE: 'department.update',
  DEPARTMENT_DELETE: 'department.delete',

  // ─── Category Permissions ────────────────────────────────────────
  CATEGORY_CREATE: 'category.create',
  CATEGORY_READ: 'category.read',
  CATEGORY_UPDATE: 'category.update',
  CATEGORY_DELETE: 'category.delete',

  // ─── Merchant Permissions ────────────────────────────────────────
  MERCHANT_CREATE: 'merchant.create',
  MERCHANT_READ: 'merchant.read',
  MERCHANT_UPDATE: 'merchant.update',
  MERCHANT_DELETE: 'merchant.delete',

  // ─── Payment Method Permissions ───────────────────────────────────
  PAYMENT_METHOD_CREATE: 'payment_method.create',
  PAYMENT_METHOD_READ: 'payment_method.read',
  PAYMENT_METHOD_UPDATE: 'payment_method.update',
  PAYMENT_METHOD_DELETE: 'payment_method.delete',

  // ─── Settings Permissions ────────────────────────────────────────
  SETTINGS_READ: 'settings.read',
  SETTINGS_MANAGE: 'settings.manage',

  // ─── Audit Permissions ───────────────────────────────────────────
  AUDIT_VIEW: 'audit.view',
  AUDIT_EXPORT: 'audit.export',

  // ─── ERP Permissions ─────────────────────────────────────────────
  ERP_CONFIGURE: 'erp.configure',
  ERP_SYNC: 'erp.sync',
  ERP_VIEW: 'erp.view',

  // ─── Dashboard Permissions ───────────────────────────────────────
  DASHBOARD_VIEW: 'dashboard.view',
  DASHBOARD_METRICS: 'dashboard.metrics',

  // ─── Notification Permissions ────────────────────────────────────
  NOTIFICATION_READ: 'notification.read',
  NOTIFICATION_MANAGE: 'notification.manage',

  // ─── System Permissions ──────────────────────────────────────────
  SYSTEM_CONFIGURE: 'system.configure',
  SYSTEM_MAINTENANCE: 'system.maintenance',
} as const;

export type PermissionName = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

// ─── Permission Groups ────────────────────────────────────────────────

export const PERMISSION_GROUPS = {
  EXPENSE_ALL: 'expense.*',
  EXPENSE_MANAGE: 'expense.manage',
  DOCUMENT_ALL: 'document.*',
  APPROVAL_ALL: 'approval.*',
  REPORT_ALL: 'report.*',
  USER_ALL: 'user.*',
  USER_MANAGE: 'user.manage',
  ROLE_ALL: 'role.*',
  COMPANY_ALL: 'company.*',
  DEPARTMENT_ALL: 'department.*',
  INTAKE_ALL: 'intake.*',
  CATEGORY_ALL: 'category.*',
  MERCHANT_ALL: 'merchant.*',
  PAYMENT_METHOD_ALL: 'payment_method.*',
  SETTINGS_ALL: 'settings.*',
  AUDIT_ALL: 'audit.*',
  ERP_ALL: 'erp.*',
  DASHBOARD_ALL: 'dashboard.*',
  NOTIFICATION_ALL: 'notification.*',
  SYSTEM_ALL: 'system.*',
  ALL: '*',
} as const;

export type PermissionGroupName = (typeof PERMISSION_GROUPS)[keyof typeof PERMISSION_GROUPS];

// ─── All Permission Names ─────────────────────────────────────────────

export const ALL_PERMISSION_NAMES: readonly string[] = Object.values(PERMISSIONS);
